// Encabezado del portal público: logos institucionales (Alcaldía de Tunja y
// MinTIC), navegación por secciones de la página de inicio y accesos a
// Ingresar / Crear cuenta.
import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar, Toolbar, Box, Button, IconButton, Drawer, List, ListItemButton, ListItemText, Divider,
  useMediaQuery, useTheme,
} from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import logoAlcaldia from "../../../activos/imagenes/nav/Logo_Alcaldía_Mayor_de_Tunja.png";
import logoMintic from "../../../activos/imagenes/nav/logo_mintic.svg";
import { useIdioma } from "../idioma/IdiomaContext";

export default function TopNavigation() {
  const theme = useTheme();
  const esEscritorio = useMediaQuery(theme.breakpoints.up("md"));
  const oscuro = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useIdioma();
  const [menuAbierto, setMenuAbierto] = React.useState(false);

  const enlaces = [
    { etiqueta: t.nav.inicio, id: "inicio" },
    { etiqueta: t.nav.comoFunciona, id: "como-funciona" },
    { etiqueta: t.nav.ruta, id: "ruta" },
    { etiqueta: t.nav.preguntas, id: "preguntas" },
  ];

  /** Lleva a la sección de la página de inicio (navega primero si estamos en otra página). */
  const irA = (id: string) => {
    setMenuAbierto(false);
    const desplazar = () =>
      id === "inicio"
        ? window.scrollTo({ top: 0, behavior: "smooth" })
        : document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (pathname !== "/") {
      navigate("/");
      setTimeout(desplazar, 120);
    } else {
      desplazar();
    }
  };

  const logos = (alto: number) => (
    <Box
      component={Link}
      to="/"
      onClick={() => window.scrollTo({ top: 0 })}
      sx={{ display: "flex", alignItems: "center", gap: 1.5, textDecoration: "none" }}
      aria-label="Inicio · Alcaldía Mayor de Tunja"
    >
      <Box
        component="img"
        src={logoAlcaldia}
        alt="Alcaldía Mayor de Tunja"
        sx={{ height: alto, width: "auto", ...(oscuro && { bgcolor: "#fff", borderRadius: 1.5, px: 0.75, py: 0.25 }) }}
      />
      <Box sx={{ width: "1px", alignSelf: "stretch", my: 1, bgcolor: "divider" }} />
      <Box
        component="img"
        src={logoMintic}
        alt="Ministerio de Tecnologías de la Información y las Comunicaciones"
        sx={{ height: alto - 6, width: "auto", filter: oscuro ? "brightness(0) invert(1)" : "none" }}
      />
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: 48,
          bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(15,23,42,0.85)" : "rgba(255,255,255,0.88)"),
          backdropFilter: "saturate(180%) blur(12px)",
          color: "text.primary",
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          zIndex: (t) => t.zIndex.appBar,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 2, md: 6 }, gap: 2 }}>
          {logos(esEscritorio ? 46 : 38)}

          {esEscritorio && (
            <Box component="nav" sx={{ flex: 1, display: "flex", justifyContent: "center", gap: 0.5 }}>
              {enlaces.map((e) => (
                <Button
                  key={e.id}
                  onClick={() => irA(e.id)}
                  sx={{ color: "text.primary", fontWeight: 600, textTransform: "none", fontSize: "0.95rem", px: 1.5, borderRadius: 999 }}
                >
                  {e.etiqueta}
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            {esEscritorio ? (
              <>
                <Button component={Link} to="/login" sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999, px: 2 }}>
                  {t.nav.ingresar}
                </Button>
                <Button
                  component={Link}
                  to="/registro"
                  variant="contained"
                  disableElevation
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999, px: 2.5 }}
                >
                  {t.nav.crearCuenta}
                </Button>
              </>
            ) : (
              <IconButton onClick={() => setMenuAbierto(true)} aria-label={t.nav.menu} edge="end">
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer anchor="right" open={menuAbierto} onClose={() => setMenuAbierto(false)}>
        <Box sx={{ width: 300, display: "flex", flexDirection: "column", height: "100%" }} role="presentation">
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2 }}>
            {logos(34)}
            <IconButton onClick={() => setMenuAbierto(false)} aria-label={t.nav.cerrar}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <List>
            {enlaces.map((e) => (
              <ListItemButton key={e.id} onClick={() => irA(e.id)}>
                <ListItemText primary={e.etiqueta} primaryTypographyProps={{ fontWeight: 600 }} />
              </ListItemButton>
            ))}
          </List>
          <Box sx={{ mt: "auto", p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Button component={Link} to="/registro" variant="contained" disableElevation onClick={() => setMenuAbierto(false)} sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}>
              {t.nav.crearCuenta}
            </Button>
            <Button component={Link} to="/login" variant="outlined" onClick={() => setMenuAbierto(false)} sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}>
              {t.nav.ingresar}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
